import {
  type CoachSessionStopPreview,
  isCoachSessionStopPreview,
  isOrderDetail,
  isSessionStartAdjustmentPreview,
  isTableSnapshot,
  type OrderDetail,
  type SessionStartAdjustmentPreview,
  type TableSnapshot,
} from "@billiard/contracts";

export type SessionBillingAction = "PAUSE" | "RESUME" | "END_EARLY" | "ADJUST_START" | "VOID";

interface SessionBillingCommandBase {
  sessionId: string;
  tableId: string;
  tableNumber: string;
  baselineOrder: OrderDetail;
  reason: string;
  idempotencyKey: string;
}

export interface SessionBillingPauseResumeCommand extends SessionBillingCommandBase {
  action: "PAUSE" | "RESUME";
}

export interface SessionEndEarlyCommand extends SessionBillingCommandBase {
  action: "END_EARLY";
  coachStopPreview: CoachSessionStopPreview | null;
  baselineTable: TableSnapshot | null;
}

export interface SessionStartAdjustmentCommand extends SessionBillingCommandBase {
  action: "ADJUST_START";
  preview: SessionStartAdjustmentPreview;
}

export interface SessionVoidCommand extends SessionBillingCommandBase {
  action: "VOID";
  baselineTable: TableSnapshot;
}

export type SessionBillingCommand =
  | SessionBillingPauseResumeCommand
  | SessionEndEarlyCommand
  | SessionStartAdjustmentCommand
  | SessionVoidCommand;

export type SessionBillingRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface SessionBillingRecovery {
  ownerIdentityKey: string;
  command: SessionBillingCommand;
  state: SessionBillingRecoveryState;
}

export type SessionBillingRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: SessionBillingRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE"; pendingOutcomeMayExist: boolean };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const SESSION_BILLING_RECOVERY_STORAGE_KEY = "billiard-erp:session-billing-recovery:v1";

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "IDEMPOTENCY_KEY_REUSED",
  "MEMBER_CHECKOUT_IN_PROGRESS",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ONLINE_SERVICE_REQUIRED",
  "ORIGIN_REJECTED",
  "PACKAGE_PAUSE_UNAVAILABLE",
  "SESSION_ALREADY_PAUSED",
  "SESSION_BILLING_ALREADY_ENDED",
  "SESSION_BILLING_NOT_RUNNING",
  "SESSION_START_ADJUSTMENT_INELIGIBLE",
  "SESSION_START_ADJUSTMENT_COMMAND_INVALID",
  "SESSION_START_ADJUSTMENT_PREVIEW_EXPIRED",
  "SESSION_START_ADJUSTMENT_PREVIEW_INVALID",
  "SESSION_START_ADJUSTMENT_PREVIEW_CHANGED",
  "SESSION_START_ADJUSTMENT_QUERY_INVALID",
  "SESSION_START_ADJUSTMENT_REQUIRES_OPEN_CASH_SHIFT",
  "SESSION_START_ADJUSTMENT_REQUIRES_UNPAID_TIME_ONLY_ORDER",
  "SESSION_START_ADJUSTMENT_HIGH_RISK_BOUNDARY",
  "SESSION_START_TIME_AFTER_BILLING_EVENT",
  "SESSION_START_TIME_INVALID",
  "SESSION_START_TIME_UNCHANGED",
  "SESSION_COACH_SERVICE_SET_CHANGED",
  "SESSION_COACH_STOP_BASELINE_REQUIRED",
  "SESSION_COACH_STOP_PREVIEW_REQUIRED",
  "SESSION_NOT_OPEN",
  "SESSION_NOT_PAUSED",
  "SESSION_OPERATION_REASON_REQUIRED",
  "SESSION_OPERATION_REASON_TOO_LONG",
  "SESSION_VOID_REASON_REQUIRED",
  "SESSION_VOID_REASON_TOO_LONG",
  "SESSION_VOID_REQUIRES_EMPTY_ORDER",
  "TABLE_NOT_FOUND",
]);

let pendingRecovery: SessionBillingRecovery | null = null;
let dispatchingRecovery: SessionBillingRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

export function sessionBillingIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function parseSessionBillingCommand(
  value: unknown,
  options: { allowLegacyEndEarlyWithoutTable?: boolean } = {},
): SessionBillingCommand | null {
  const isVoid = isRecord(value) && value.action === "VOID";
  const isEndEarly = isRecord(value) && value.action === "END_EARLY";
  const isAdjustStart = isRecord(value) && value.action === "ADJUST_START";
  const hasCoachStopPreview = isEndEarly && Object.hasOwn(value, "coachStopPreview");
  const hasEndEarlyBaselineTable = isEndEarly && Object.hasOwn(value, "baselineTable");
  const isLegacyEndEarly =
    isEndEarly && options.allowLegacyEndEarlyWithoutTable === true && !hasEndEarlyBaselineTable;
  if (
    !isRecord(value) ||
    !hasExactKeys(
      value,
      isVoid
        ? [
            "action",
            "sessionId",
            "tableId",
            "tableNumber",
            "baselineOrder",
            "reason",
            "idempotencyKey",
            "baselineTable",
          ]
        : isEndEarly && !isLegacyEndEarly
          ? [
              "action",
              "sessionId",
              "tableId",
              "tableNumber",
              "baselineOrder",
              "reason",
              "idempotencyKey",
              "coachStopPreview",
              "baselineTable",
            ]
          : isEndEarly && hasCoachStopPreview
            ? [
                "action",
                "sessionId",
                "tableId",
                "tableNumber",
                "baselineOrder",
                "reason",
                "idempotencyKey",
                "coachStopPreview",
              ]
            : isAdjustStart
              ? [
                  "action",
                  "sessionId",
                  "tableId",
                  "tableNumber",
                  "baselineOrder",
                  "reason",
                  "idempotencyKey",
                  "preview",
                ]
              : [
                  "action",
                  "sessionId",
                  "tableId",
                  "tableNumber",
                  "baselineOrder",
                  "reason",
                  "idempotencyKey",
                ],
    ) ||
    (value.action !== "PAUSE" &&
      value.action !== "RESUME" &&
      value.action !== "END_EARLY" &&
      value.action !== "ADJUST_START" &&
      value.action !== "VOID") ||
    !isBoundedString(value.sessionId, 1, 256) ||
    !isBoundedString(value.tableId, 1, 256) ||
    !isBoundedString(value.tableNumber, 1, 64) ||
    value.sessionId !== value.sessionId.trim() ||
    value.tableId !== value.tableId.trim() ||
    value.tableNumber !== value.tableNumber.trim() ||
    !isOrderDetail(value.baselineOrder) ||
    value.baselineOrder.status !== "OPEN" ||
    value.baselineOrder.sessionId !== value.sessionId ||
    value.baselineOrder.tableId !== value.tableId ||
    value.baselineOrder.tableNumber !== value.tableNumber ||
    !isBoundedString(value.reason, 2, 200) ||
    value.reason !== value.reason.trim() ||
    !isBoundedString(value.idempotencyKey, 8, 128) ||
    value.idempotencyKey !== value.idempotencyKey.trim()
  ) {
    return null;
  }
  if (value.baselineOrder.endedAt !== null) return null;
  const base: SessionBillingCommandBase = {
    sessionId: value.sessionId,
    tableId: value.tableId,
    tableNumber: value.tableNumber,
    baselineOrder: value.baselineOrder,
    reason: value.reason,
    idempotencyKey: value.idempotencyKey,
  };
  if (value.action === "VOID") {
    const groupBuyFulfillments = value.baselineOrder.groupBuyFulfillments ?? [];
    return isTableSnapshot(value.baselineTable) &&
      value.baselineTable.state === "IN_USE" &&
      value.baselineTable.sessionId === value.sessionId &&
      value.baselineTable.sessionStartedAt === value.baselineOrder.startedAt &&
      value.baselineTable.tableId === value.tableId &&
      value.baselineTable.tableNumber === value.tableNumber &&
      value.baselineTable.billingState === value.baselineOrder.billingState &&
      value.baselineTable.billingPausedAt === value.baselineOrder.billingPausedAt &&
      value.baselineTable.pricingPlanName === value.baselineOrder.priceSnapshot.name &&
      value.baselineTable.estimatedFeeFen === value.baselineOrder.tableFeeFen &&
      value.baselineTable.currentTotalFen === value.baselineOrder.totalFen &&
      (value.baselineOrder.billingState === "RUNNING" ||
        value.baselineOrder.billingState === "PAUSED") &&
      (value.baselineOrder.billingState === "PAUSED"
        ? value.baselineOrder.billingPausedAt !== null
        : value.baselineOrder.billingPausedAt === null) &&
      value.baselineOrder.productTotalFen === 0 &&
      value.baselineOrder.coachServiceTotalFen === 0 &&
      value.baselineOrder.paidFen === 0 &&
      value.baselineOrder.refundedFen === 0 &&
      value.baselineOrder.items.length === 0 &&
      value.baselineOrder.coachServiceItems.length === 0 &&
      groupBuyFulfillments.length === 0 &&
      value.baselineOrder.payments.length === 0 &&
      value.baselineOrder.refunds.length === 0
      ? { ...base, action: "VOID", baselineTable: value.baselineTable }
      : null;
  }
  if (value.action === "ADJUST_START") {
    const preview = value.preview;
    const noOtherBusinessFacts =
      value.baselineOrder.productTotalFen === 0 &&
      value.baselineOrder.coachServiceTotalFen === 0 &&
      value.baselineOrder.paidFen === 0 &&
      value.baselineOrder.refundedFen === 0 &&
      value.baselineOrder.items.length === 0 &&
      value.baselineOrder.coachServiceItems.length === 0 &&
      (value.baselineOrder.groupBuyFulfillments ?? []).length === 0 &&
      value.baselineOrder.payments.length === 0 &&
      value.baselineOrder.refunds.length === 0;
    return isSessionStartAdjustmentPreview(preview) &&
      preview.sessionId === value.sessionId &&
      preview.orderId === value.baselineOrder.id &&
      preview.tableId === value.tableId &&
      preview.tableNumber === value.tableNumber &&
      preview.billingState === value.baselineOrder.billingState &&
      preview.previousStartedAt === value.baselineOrder.startedAt &&
      preview.previousBusinessDate === value.baselineOrder.businessDate &&
      preview.proposedBusinessDate === value.baselineOrder.businessDate &&
      preview.cashShiftBusinessDate === value.baselineOrder.businessDate &&
      preview.notificationAudience[0] === "OWNER" &&
      preview.notificationAudience[1] === "MANAGER" &&
      preview.notificationChannel === "BUSINESS_OPERATION_LOG" &&
      preview.notificationOutcome === "RECORDED_IN_SAME_TRANSACTION" &&
      preview.previousTableFeeFen >= value.baselineOrder.tableFeeFen &&
      (value.baselineOrder.billingState === "RUNNING" ||
        value.baselineOrder.billingState === "PAUSED") &&
      (value.baselineOrder.billingState === "PAUSED"
        ? value.baselineOrder.billingPausedAt !== null
        : value.baselineOrder.billingPausedAt === null) &&
      noOtherBusinessFacts
      ? { ...base, action: "ADJUST_START", preview }
      : null;
  }
  if (value.action === "RESUME") {
    return value.baselineOrder.billingState === "PAUSED" &&
      value.baselineOrder.billingPausedAt !== null
      ? { ...base, action: "RESUME" }
      : null;
  }
  if (
    value.baselineOrder.billingState !== "RUNNING" ||
    value.baselineOrder.billingPausedAt !== null
  ) {
    return null;
  }
  if (value.action === "END_EARLY") {
    const coachStopPreview = hasCoachStopPreview ? value.coachStopPreview : null;
    const baselineTable = hasEndEarlyBaselineTable ? value.baselineTable : null;
    if (
      coachStopPreview !== null &&
      (!isCoachSessionStopPreview(coachStopPreview) ||
        coachStopPreview.sessionId !== value.sessionId ||
        coachStopPreview.tableId !== value.tableId ||
        coachStopPreview.tableNumber !== value.tableNumber)
    ) {
      return null;
    }
    if (
      baselineTable !== null &&
      (!isTableSnapshot(baselineTable) ||
        baselineTable.state !== "IN_USE" ||
        baselineTable.sessionId !== value.sessionId ||
        baselineTable.sessionStartedAt !== value.baselineOrder.startedAt ||
        baselineTable.tableId !== value.tableId ||
        baselineTable.tableNumber !== value.tableNumber ||
        baselineTable.billingState !== "RUNNING" ||
        baselineTable.billingPausedAt !== null ||
        baselineTable.pricingPlanName !== value.baselineOrder.priceSnapshot.name ||
        baselineTable.estimatedFeeFen !== value.baselineOrder.tableFeeFen ||
        baselineTable.currentTotalFen !== value.baselineOrder.totalFen)
    ) {
      return null;
    }
    return { ...base, action: "END_EARLY", coachStopPreview, baselineTable };
  }
  return { ...base, action: "PAUSE" };
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: SessionBillingCommand,
  state: SessionBillingRecoveryState,
): SessionBillingRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function storedRecovery(value: unknown): SessionBillingRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    (value.version !== 1 && value.version !== 2 && value.version !== 3) ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(value.recovery.ownerIdentityKey) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  const command = parseSessionBillingCommand(value.recovery.command, {
    allowLegacyEndEarlyWithoutTable: value.version < 3,
  });
  if (!command) return null;
  return immutableRecovery(value.recovery.ownerIdentityKey, command, value.recovery.state);
}

function recoveryEquals(left: SessionBillingRecovery, right: SessionBillingRecovery): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): SessionBillingRecovery | null {
  const encoded = storage.getItem(SESSION_BILLING_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: SessionBillingRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      SESSION_BILLING_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 3, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: SessionBillingRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(SESSION_BILLING_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readSessionBillingRecovery(identityKey: string): SessionBillingRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimSessionBillingRecovery(
  identityKey: string,
  command: SessionBillingCommand,
): SessionBillingRecoveryView {
  const current = readSessionBillingRecovery(identityKey);
  if (current.status !== "NONE") return current;
  const parsedCommand = parseSessionBillingCommand(command);
  if (!isIdentityKey(identityKey) || !parsedCommand) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  const recovery = immutableRecovery(identityKey, parsedCommand, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(recovery: SessionBillingRecovery, identityKey: string): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginSessionBillingDispatch(
  recovery: SessionBillingRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishSessionBillingDispatch(
  recovery: SessionBillingRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markSessionBillingOutcomeUnknown(
  recovery: SessionBillingRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

export function clearSessionBillingRecovery(
  recovery: SessionBillingRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(SESSION_BILLING_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(SESSION_BILLING_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function sessionBillingOutcomeIsUnknown(error: unknown): boolean {
  return !(
    isRecord(error) &&
    typeof error.code === "string" &&
    definiteNoEffectCodes.has(error.code)
  );
}

export function resetSessionBillingRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(SESSION_BILLING_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetSessionBillingRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
